import type { EChartsOption } from 'echarts';

/**
 * ECharts options for the competency radar (pure, tested in
 * competency-radar.options.spec.ts). Ordinal scale: three rings — im Aufbau,
 * sicher, gefestigt — never percentages. Series colours from the chart
 * palette (`echart-theme.json`); rings and labels in the CI neutrals.
 */

export interface RadarAxisInput {
  key: string;
  name: string;
  /** 0..3 */
  level: number;
  /** 0..3 — measured / total on the same scale */
  coverage: number;
  /** not captured / open: the name is drawn quieter */
  muted: boolean;
  levelLabel: string;
}

export interface RadarTexts {
  level: string;
  coverage: string;
  /** inner → outer: im Aufbau, sicher, gefestigt */
  rings: [string, string, string];
}

export interface RadarSize {
  width: number;
  height: number;
}

export const RADAR_LEVEL_COLOR = '#007BFF';
export const RADAR_COVERAGE_COLOR = '#001F7A';
const TEXT = '#243650';
const MUTED = '#526581';
const NAME_WRAP = 18;

export function escapeHtml(value: string): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Break a long axis name at a space near `width` characters (two lines at
 *  most) — never inside a word, never right after the "2 · " number. */
export function wrapName(name: string, width = NAME_WRAP): string {
  if (name.length <= width) return name;
  const prefix = /^\d+ · /.exec(name)?.[0].length ?? 0;
  let cut = name.lastIndexOf(' ', width);
  if (cut < prefix) cut = name.indexOf(' ', prefix);
  if (cut === -1) return name.length <= width + 6 ? name : name.slice(0, width + 5).trimEnd() + '…';
  let rest = name.slice(cut + 1);
  if (rest.length > width) rest = rest.slice(0, width - 1).trimEnd() + '…';
  return `${name.slice(0, cut)}\n${rest}`;
}

/** Centre and radius in px — the ring names are placed with them. */
export function radarGeometry(size: RadarSize): { cx: number; cy: number; radius: number } {
  const w = Math.max(160, size.width);
  const h = Math.max(160, size.height);
  // Room for the axis names around the net: ~110px across, ~70px down.
  const radius = Math.max(50, Math.min((w - 220) / 2, (h - 80) / 2));
  return { cx: w / 2, cy: h / 2 + 4, radius };
}

export function buildRadarOptions(
  axes: RadarAxisInput[], texts: RadarTexts, size: RadarSize,
  opts: { showCoverage: boolean; reducedMotion: boolean },
): EChartsOption {
  const { cx, cy, radius } = radarGeometry(size);
  const series: any[] = [
    {
      type: 'radar',
      name: texts.level,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { color: RADAR_LEVEL_COLOR, width: 2 },
      itemStyle: { color: RADAR_LEVEL_COLOR },
      areaStyle: { color: 'rgba(0, 123, 255, 0.16)' },
      data: [{ name: texts.level, value: axes.map((a) => a.level) }],
      z: 3,
    },
  ];
  if (opts.showCoverage) {
    series.push({
      type: 'radar',
      name: texts.coverage,
      symbol: 'none',
      lineStyle: { color: RADAR_COVERAGE_COLOR, width: 1.5, type: 'dashed' },
      areaStyle: { opacity: 0 },
      data: [{ name: texts.coverage, value: axes.map((a) => Math.round(a.coverage * 100) / 100) }],
      z: 2,
      silent: true,
    });
  }
  // Ring names along the first axis (straight up), just right of it.
  const graphic = texts.rings.map((ring, i) => ({
    type: 'text',
    silent: true,
    left: cx + 6,
    top: cy - (radius * (i + 1)) / 3 - 7,
    style: {
      text: ring, fill: MUTED, fontSize: 11, fontFamily: 'Geist, sans-serif',
      // Readable where the shape crosses the first axis.
      backgroundColor: 'rgba(255, 255, 255, 0.78)', padding: [1, 3], borderRadius: 3,
    },
    z: 10,
  }));
  return {
    animation: !opts.reducedMotion,
    textStyle: { fontFamily: 'Geist, sans-serif' },
    tooltip: {
      trigger: 'item',
      confine: true,
      backgroundColor: 'rgba(30, 30, 40, 0.92)',
      borderWidth: 0,
      textStyle: { color: '#fff', fontSize: 13 },
      formatter: () =>
        axes.map((a) => `${escapeHtml(a.name)}: <b>${escapeHtml(a.levelLabel)}</b>`).join('<br/>'),
    },
    radar: {
      shape: 'polygon',
      center: [cx, cy],
      radius,
      startAngle: 90,
      splitNumber: 3,
      triggerEvent: true,
      indicator: axes.map((a) => ({
        name: wrapName(a.name),
        max: 3,
        min: 0,
        color: a.muted ? MUTED : TEXT,
      })),
      axisName: { fontSize: 12, lineHeight: 15 },
      splitArea: {
        areaStyle: {
          color: ['rgba(59, 130, 246, 0.03)', 'rgba(59, 130, 246, 0.06)', 'rgba(59, 130, 246, 0.09)'],
        },
      },
      splitLine: { lineStyle: { color: 'rgba(82, 101, 129, 0.28)' } },
      axisLine: { lineStyle: { color: 'rgba(82, 101, 129, 0.28)' } },
    } as any,
    graphic,
    series,
  };
}
